import { extendTheme } from '@chakra-ui/react';
import { ButtonStyle as Button } from './partials/global/ButtonStyle';
import { HeadingStyle as Heading } from './partials/global/HeadingStyle';
import { TextStyle as Text } from './partials/global/TextStyle';
import { CheckboxStyle as Checkbox } from './partials/global/CheckboxStyle';
import { InputStyle as Input } from './partials/global/InputStyle';
import { SelectStyle as Select } from './partials/global/InputStyle';
import { RadioStyle as Radio } from './partials/global/InputStyle';

/**
 * One typography + palette (RSW-600 / Launch Hardening P2.1).
 *
 * Every value below maps to the workspace kit (workspaceKitTheme.css /
 * workspaceTokens.js) so the remaining Chakra surfaces render the same
 * design language as the kit-token workspace — one font family (Poppins,
 * loaded via index.html), one palette (cream / navy / rose / sienna).
 * Token names are unchanged so no component code had to move.
 */

// Kit type stack (matches --rs-font-sans).
const KIT_FONT = "'Poppins', 'Inter', 'Helvetica Neue', Arial, sans-serif";

// Kit anchors: navy #1E2A4A (structure), royal #3B5998 (info/focus),
// sienna #D16A24 (primary action), rose #C4187A (brand/progress).
const NAVY_SCALE = {
  50: '#ECF0F8',
  100: '#D5DEEF',
  200: '#AEBEDD',
  300: '#7C93BE',
  400: '#3B5998',
  500: '#1E2A4A',
  600: '#182238',
  700: '#131C33',
  800: '#0E1526',
  900: '#090E1A',
};

const SIENNA_SCALE = {
  50: '#FDF3E7',
  100: '#F9E3CB',
  200: '#F2C9A0',
  300: '#E9A566',
  400: '#D16A24',
  // Solid Chakra buttons put white text on 500 and hover on 600: 500 must
  // clear 4.5:1 (#D16A24 is 3.6:1), so both shift one step darker (RSW-613).
  500: '#B65A1C',
  600: '#964A17',
  700: '#964A17',
  800: '#773B12',
  900: '#582C0E',
};

const rstheme = extendTheme({
  fonts: {
    body: KIT_FONT,
    heading: KIT_FONT,
    text: KIT_FONT,
    link: KIT_FONT,
  },

  colors: {
    // Legacy tokens (names kept for backward compat; values now kit).
    WHITE: '#FFFFFF',
    BLACK: '#2D2D2D',
    BLACKER: '#2D2D2D',
    GRAY: '#D9D2C7',
    GRAY_2: '#6C6862',
    GRAY_DARK: '#2D2D2D',
    GRAY_LIGHT: '#F7F5F2',
    NAVY: '#1E2A4A',
    NAVY_LIGHT: '#1E2A4A',
    NAVY_EXTRA_LIGHT: '#ECF0F8',
    NAVY_HOVER: '#131C33',
    ORANGE: '#D16A24',
    ORANGE_TEXT: '#B65A1C',
    ORANGE_LIGHT: '#FDF3E7',
    ORANGE_DARK: '#B65A1C',
    ORANGE_HOVER: '#B65A1C',
    MAGENTA: '#C4187A',
    RED: '#B6304B',
    BLUE: '#3B5998',
    orangeScheme: {
      100: '#F9E3CB',
      500: '#D16A24',
      900: '#964A17',
    },

    // Semantic brand palette → kit navy.
    brand: NAVY_SCALE,
    // In-progress state only. Navy stays structural, sienna stays for actions,
    // so rose reads as "something is running" wherever it appears.
    magenta: {
      50: '#FDF0F6',
      500: '#C4187A',
      600: '#A8136A',
    },
    // Primary-action palette → kit sienna.
    accent: SIENNA_SCALE,

    // Chakra built-in schemes used via colorScheme="blue" / "orange" across
    // the shell and counselor surfaces — overridden so those call sites
    // inherit the kit without touching each one.
    blue: NAVY_SCALE,
    orange: SIENNA_SCALE,
  },

  semanticTokens: {
    colors: {
      'brand.primary': 'brand.500',
      'brand.accent': 'accent.400',
      'brand.accent.hover': 'accent.600',
      'brand.accent.subtle': 'accent.100',
      'brand.surface': '#FFFFFF',
      'brand.text': '#2D2D2D',
      'brand.text.secondary': '#4A4A4A',
      'brand.text.muted': '#6C6862', // AA on cream (RSW-613)
      'brand.border': '#E7E2DA',
      'brand.bg.page': '#F7F5F2',
      'brand.bg.card': '#FFFFFF',
    },
  },

  sizes: {
    contentMax: '960px',
    contentWide: '1200px',
  },

  styles: {
    global: {
      body: {
        bg: 'brand.bg.page',
        color: 'brand.text',
      },
    },
  },

  components: {
    Button,
    Heading,
    Text,
    Checkbox,
    Input,
    Select,
    Radio,
  },
});

export default rstheme;
